import type { IncentivesRecord } from "@dealer-center/ddc-pages-incentives-manager-interface";
import type { RootNode } from "@dealer-center/ddc-pages-incentives-manager-lambda";
import { doesArraysHaveCommonString, onlyUniqueEntries } from "./utils";

export interface MapTreeNode {
  labels: string[];
  active: (boolean | null)[];
  nodes?: MapTreeNode[];
  trimIds?: string[][];
}

export interface Filters {
  condition: string | null; // null, "NEW", "CPO"
  make: string | null; // null, "Ford", "Chevrolet", etc.
  year: number | null; // null, 2019, etc.
  model: string | null; // null, "F-150", "Silverado", etc.
  trim: string | null; // null, <made up of uniqueTrim | driveTrain> will use an entry into the list of trims
  years: number[] | null; // null, 2019, etc.
  freeWords: string[] | null;
  scratchText: string;
  type: string | null;
  source: string | null;
}
export type Filter = string | number | null;

export type VehicleFilter = string | number | string[] | number[] | null;

type NavMap = Map<string, boolean>;

// Help walk down the navtree (which no longer has conditions in it)
//
const LEVELS = ["make", "year", "model", "trim"];

// Allow case-insensitive, partial matches (future-proofing for free-text search input)
//
export function isFilterMatch(
  nodeLabel: string,
  filter: Filter,
  allowPartials = false
) {
  if (allowPartials) {
    return (
      !filter ||
      nodeLabel?.toLowerCase().includes(filter.toString().toLowerCase())
    );
  }
  return (
    !filter || nodeLabel?.toLowerCase() === filter.toString().toLowerCase()
  );
}

function makeMatchNode(
  filters: Filters,
  map: NavMap,
  maxLevel: number,
  onMaxLevelReached: (node, map: NavMap, filter: Filter) => void,
  allowPartials = false
) {
  const matchNode = (node, level = 0) => {
    const filter: Filter = filters[LEVELS[level]]?.toString() || null;
    if (level < maxLevel) {
      node?.children?.forEach(child => {
        if (isFilterMatch(child.label, filter, allowPartials)) {
          matchNode(child, level + 1);
        }
      });
    } else {
      onMaxLevelReached(node, map, filter);
    }
  };
  return matchNode;
}

function onTrimIdsMaxLevelReached(node, map: NavMap) {
  if (node.gvTrimIds) {
    node.gvTrimIds.forEach(value => {
      map.set(value, true);
    });
  }
}

// From a trim nav tree and a Filter object(condition, make, year, model, trim), return a list of trimIds
// if no filters are set, return null
//
export function getTrimIdList(
  navtree,
  filters: Filters,
  allowPartials = false
): string[] | null {
  const { condition, make, year, model, trim } = filters;
  if (!condition && !make && !year && !model && !trim) {
    return null;
  }
  const map: NavMap = new Map(); // If we knew that leaf node trimIds were unique, we could push to a list directly

  const matchNode = makeMatchNode(
    filters,
    map,
    4,
    onTrimIdsMaxLevelReached,
    allowPartials
  );
  matchNode(navtree);
  return Array.from(map.keys());
}

function isYearMatch(
  incentive: IncentivesRecord,
  years: number[] | null | undefined
) {
  if (!years || years.length <= 1) {
    return true;
  }
  let isFound = false;
  if (incentive.applicableYear !== -1) {
    isFound = years.includes(incentive.applicableYear);
  } else {
    isFound = years.some(year => {
      const uniqueYears = onlyUniqueEntries(
        incentive.applicableVehicleList.map(v => v.year)
      );
      const isYes = uniqueYears.some(vehicleYear => vehicleYear === year);
      return isYes;
    });
  }
  return isFound;
}

export function cleanString(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9,$.\-%éïàèùâêîôûëïüäç']/gi, " ");
}

function isFreeWordsMatch(
  incentive: IncentivesRecord,
  freeWords: string[] | null | undefined
) {
  if (!freeWords || freeWords.length === 0) {
    return true;
  }
  const models = incentive.applicableVehicleList
    .map(v => `${v.model} ${v.bodyStyle}`)
    .join(" ");
  const contentStr: string =
    cleanString(incentive.content.title || "") + // Offer
    " " +
    cleanString(incentive.content.shortTitle || "") + // Short Title
    " " +
    cleanString(models);

  let wordCount = 0;
  let lastWordidx = -1;
  freeWords.forEach(word => {
    const inIdx = contentStr.indexOf(word, lastWordidx);
    if (inIdx === 0 || (inIdx > 0 && contentStr[inIdx - 1] === " ")) {
      lastWordidx = inIdx + word.length;
      wordCount++;
    }
  });
  return wordCount >= freeWords.length;
}

function isTypeMatch(
  incentive: IncentivesRecord,
  type: string | null | undefined
): boolean {
  if (!type || type === "ALL") {
    return true;
  }

  return incentive.type?.toUpperCase() === type.toUpperCase();
}
// Given a list of incentives and a list of trimIds, return a list of incentives that have at least one trimId in the list
//
export function getFilteredIncentives(
  incentives: IncentivesRecord[],
  trimIdList: string[] | null,
  filters: Filters
): IncentivesRecord[] {
  const { condition, type, source } = filters;

  return incentives.filter((incentive, idx) => {
    let isMatch = false;

    // condition filter check
    let conditionMatch = true;
    if (condition && condition !== "ALL") {
      conditionMatch =
        condition.toUpperCase() === incentive.applicableCondition.toUpperCase();
    }

    // type check
    let typeMatch = true;
    if (type && type !== "ALL") {
      typeMatch = isTypeMatch(incentive, type);
    }

    // source check
    let sourceMatch = true;
    if (source && source !== "ALL") {
      const sourceValue = source.startsWith('"') ? JSON.parse(source) : source;
      sourceMatch =
        incentive.creatorAccountName?.toLowerCase() ===
        sourceValue.toLowerCase();
    }
    if (conditionMatch && typeMatch && sourceMatch) {
      // If no vehicle filters are set, include all incentives that pass condition/type/source
      if (trimIdList === null) {
        isMatch = true; // Free search or no vehicle filters
      } else {
        // Apply vehicle filter constraints
        const trims = incentive?.globalVehicleTrimIdList || [];
        if (trims.length > 0) {
          isMatch = trims.some((trimId: string): boolean =>
            trimIdList.includes(trimId)
          );
        }
      }
      // Apply existing free text and year filtering if vehicle match found
      if (isMatch) {
        isMatch =
          isYearMatch(incentive, filters.years) &&
          isFreeWordsMatch(incentive, filters.freeWords);
      }
    }
    return isMatch;
  });
}

function onSelectMaxLevelReached(node, map: NavMap, filter: Filter) {
  if (isFilterMatch(node.label, filter)) {
    map.set(node.label, true);
  }
}

export function getMakes(
  navtree,
  filters: Filters | undefined = undefined,
  isMakeLocked: (make: string) => boolean | undefined = () => false
): string[] {
  const map: NavMap = new Map();
  const fixFilters = filters
    ? { ...filters, make: null }
    : ({ condition: null, make: null, model: null, trim: null } as Filters);
  const matchNode = makeMatchNode(fixFilters, map, 1, onSelectMaxLevelReached);
  matchNode(navtree);
  return Array.from(map.keys())
    .filter(make => !isMakeLocked(make))
    .sort();
}

export function getYears(navtree, filters: Filters): number[] {
  const map: NavMap = new Map();
  const fixFilters = { ...filters, year: null };
  const matchNode = makeMatchNode(fixFilters, map, 2, onSelectMaxLevelReached);
  matchNode(navtree);
  const results = (Array.from(map.keys()) as unknown[] as string[])
    .map((year: string): number => parseInt(year, 10))
    .sort();
  return results;
}

export function getModels(navtree, filters: Filters): string[] {
  const map: NavMap = new Map();
  const fixFilters = { ...filters, model: null };
  const matchNode = makeMatchNode(fixFilters, map, 3, onSelectMaxLevelReached);
  matchNode(navtree);
  return (Array.from(map.keys()) as unknown[] as string[]).sort();
}

export function getTrims(navtree, filters: Filters): string[] {
  const map: NavMap = new Map();
  const fixFilters = { ...filters, trim: null };
  const matchNode = makeMatchNode(fixFilters, map, 4, onSelectMaxLevelReached);
  matchNode(navtree);
  return (Array.from(map.keys()) as unknown[] as string[]).sort();
}

export function getMakeTree(
  navtree: RootNode | null,
  make: string | null,
  isActive = true
): MapTreeNode {
  const map: MapTreeNode = { labels: [], active: [], nodes: [] };
  if (!navtree) {
    return map;
  }
  const makeNode = navtree.children.find(
    child => child.label.toLowerCase() === make?.toLowerCase()
  );
  if (makeNode) {
    makeNode.children.forEach(yearNode => {
      map.labels.push(yearNode.label);
      const yearNodeMap: MapTreeNode = { labels: [], active: [], nodes: [] };
      if (map.nodes) {
        map.nodes.push(yearNodeMap);
        map.active.push(isActive);
        yearNode.children.forEach(modelNode => {
          const modelNodeMap: MapTreeNode = {
            labels: [],
            active: [],
            trimIds: []
          };
          yearNodeMap.labels.push(modelNode.label);
          yearNodeMap.active.push(isActive);
          if (yearNodeMap.nodes) {
            yearNodeMap.nodes.push(modelNodeMap);
            modelNode.children.forEach(trimNode => {
              modelNodeMap.labels.push(trimNode.label);
              modelNodeMap.active.push(isActive);
              modelNodeMap.trimIds?.push(
                trimNode.gvTrimIds ? [...trimNode.gvTrimIds] : []
              );
            });
          }
        });
      }
    });
  }
  return map;
}

export function getTrimIdsFromFilters(
  makeTree: MapTreeNode,
  filters: Filters
): string[] {
  const trimIds: string[] = [];
  if (!makeTree || !filters) {
    return trimIds;
  }

  const findYearTrims = (yearIndex: number) => {
    if (makeTree.nodes) {
      makeTree.nodes.forEach((modelNode, yearIdx) => {
        if (yearIndex === -1 || yearIndex === yearIdx) {
          if (modelNode.nodes) {
            const modelIndex = modelNode.labels.indexOf(filters.model || "");
            modelNode.nodes.forEach((trimNode, modelIdx) => {
              if (modelIndex === -1 || modelIndex === modelIdx) {
                if (trimNode.trimIds) {
                  const trimIndex = trimNode.labels.indexOf(filters.trim || "");
                  trimNode.trimIds.forEach((trimIdList, trimIdx) => {
                    if (
                      trimIdList &&
                      (trimIndex === -1 || trimIndex === trimIdx)
                    ) {
                      trimIds.push(...trimIdList);
                    }
                  });
                }
              }
            });
          }
        }
      });
    }
  };

  if (makeTree.nodes) {
    if (filters.years && filters.years.length > 0) {
      filters.years.forEach(year => {
        const yearIndex = makeTree.labels.indexOf(year.toString());
        findYearTrims(yearIndex);
      });
    } else {
      const year = filters.year?.toString() || "";
      const yearIndex = makeTree.labels.indexOf(year);
      findYearTrims(yearIndex);
    }
  }
  return trimIds;
}

// Some facts about an array of booleans and nulls
//
export function arrayActivity(node) {
  const nActive = node.active.reduce((acc, val) => acc + (val ? 1 : 0), 0);
  const isAllActive = nActive === node.active.length;
  const isNoneActive = nActive === 0;
  const isSomeActive = !isAllActive && nActive > 0;
  const nNulls = node.active.reduce(
    (acc, val) => acc + (val === null ? 1 : 0),
    0
  );
  return { isAllActive, isSomeActive, isNoneActive, nNulls };
}

export function setMakeTreeByTrimIds(
  makeTree: MapTreeNode,
  trimIds: string[]
): MapTreeNode {
  if (!makeTree || !trimIds || trimIds.length === 0) {
    return makeTree;
  }
  const newMakeTree = { ...makeTree };

  if (newMakeTree.nodes) {
    newMakeTree.nodes.forEach((modelNode, yearIdx) => {
      if (modelNode.nodes) {
        modelNode.nodes.forEach((trimNode, modelIdx) => {
          if (trimNode.trimIds) {
            trimNode.trimIds.forEach((trimIdList, trimIdx) => {
              const isActive = doesArraysHaveCommonString(trimIds, trimIdList);
              trimNode.active[trimIdx] = isActive;
            });
            const { isAllActive, isNoneActive } = arrayActivity(trimNode);
            modelNode.active[modelIdx] = isAllActive
              ? true
              : isNoneActive
                ? false
                : null;
          }
        });
        const { isAllActive, isSomeActive, nNulls } = arrayActivity(modelNode);
        newMakeTree.active[yearIdx] = isAllActive
          ? true
          : isSomeActive || nNulls
            ? null
            : false;
      }
    });
  }
  return newMakeTree;
}

export function getActiveTrimIds(makeTree: MapTreeNode): string[] {
  const activeTrimIds: string[] = [];
  if (!makeTree || !makeTree.nodes) {
    return activeTrimIds;
  }
  makeTree.nodes.forEach((modelNode, yearIdx) => {
    if (modelNode.nodes && makeTree.active[yearIdx] !== false) {
      modelNode.nodes.forEach((trimNode, modelIdx) => {
        if (trimNode.trimIds) {
          trimNode.trimIds.forEach((trimIdList, trimIdx) => {
            if (trimNode.active[trimIdx]) {
              activeTrimIds.push(...trimIdList);
            }
          });
        }
      });
    }
  });
  return activeTrimIds;
}
